import type { SprintRoute } from '@waago/api'

/**
 * Which sprint delivers each screen of the back office.
 *
 * <p>Derived from `WaaGo_Matrice_Fonctionnalites_Sprints.xlsx`. Login and
 * password reset (BO-001/002) are treated as pre-existing baseline — the
 * matrix never assigns them to a milestone, and the contract's M1 text
 * implies basic auth already worked before day 1 — so they stay in Sprint 1
 * alongside the client app's own baseline screens. Everything else in the
 * back office is M7 (Sprint 3) or later: client/provider/mission
 * administration, payments and disputes are all back-office-completion work
 * that the roadmap deliberately puts late, since it reviews what earlier
 * milestones create.
 *
 * <p>Judgement calls:
 *
 * - **`/parametres` and `/mon-compte` are Sprint 1** — a signed-in staff
 *   member changing their own password is the same class of self-service as
 *   BO-002; the SUPER_ADMIN internal-user-CRUD panel behind the same route
 *   (BO-003, Sprint 3) is gated by role inside the page, not by this table.
 * - **`/vehicules` and `/abonnements` are both Sprint 3**, matching BO-018
 *   ("Véhicules + abonnements", one row covering both), which the matrix's
 *   own M7 text names explicitly alongside "services, plans, advertising".
 * - **Payments (BO-015/016) are Sprint 3** — thematically M6 money work, but
 *   M6 lands in the same sprint as M7 back-office completion, so the sprint
 *   boundary doesn't actually depend on this call.
 */
export const BACKOFFICE_SPRINTS: SprintRoute[] = [
  // --- Sprint 1 · baseline auth -------------------------------------------
  { path: '/connexion', sprint: 1 }, // BO-001 Login admin
  { path: '/mot-de-passe-oublie', sprint: 1 }, // BO-002 Reset MDP
  { path: '/reinitialiser-mot-de-passe', sprint: 1 },
  { path: '/parametres', sprint: 1 },
  { path: '/mon-compte', sprint: 1 },

  // --- Sprint 3 · M5 Tracking, M6 Money, M7 Back-Office -------------------
  { path: '/', sprint: 3 }, // BO-004/005 Dashboard KPIs
  { path: '/clients', sprint: 3 }, // BO-007/008
  { path: '/clients/:id', sprint: 3 },
  { path: '/prestataires', sprint: 3 }, // BO-009..011
  { path: '/prestataires/:id', sprint: 3 },
  { path: '/missions', sprint: 3 }, // BO-012/013
  { path: '/missions/:id', sprint: 3 },
  { path: '/services', sprint: 3 }, // BO-017 Services CRUD
  { path: '/services/:activity', sprint: 3 },
  { path: '/vehicules', sprint: 3 }, // BO-018 Véhicules + abonnements
  { path: '/abonnements', sprint: 3 },
  { path: '/abonnements/:code', sprint: 3 },
  { path: '/paiements', sprint: 3 }, // BO-015/016
  { path: '/paiements/:id', sprint: 3 },
  { path: '/signalements', sprint: 3 }, // BO-014 Signalements & litiges
  { path: '/signalements/:id', sprint: 3 },
  { path: '/publicite', sprint: 3 }, // BO-019 Publicité
  { path: '/publicite/:id', sprint: 3 },

  // --- Sprint 4 · M8 Notifications & Support ------------------------------
  { path: '/notifications', sprint: 4 }, // BO-006 Alertes
]
